import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

interface Message {
  id: string;
  name: string;
  content: string;
  content_type: 'text';
  created_at: string;
  is_approved: boolean;
}

interface UseMessageManagementReturn {
  messages: Message[];
  loading: boolean;
  error: string | null;
  toggleApproval: (messageId: string, currentStatus: boolean) => Promise<void>;
  deleteMessage: (messageId: string) => Promise<void>;
  approveAll: () => Promise<void>;
  refreshMessages: () => Promise<void>;
}

export function useMessageManagement(): UseMessageManagementReturn {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const { data, error: fetchError } = await supabase
        .from('messages')
        .select('*')
        .order('created_at', { ascending: false });
        
      if (fetchError) throw fetchError;
      setMessages(data || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching messages:', err);
      setError('Failed to fetch messages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
  }, []);

  const toggleApproval = async (messageId: string, currentStatus: boolean) => {
    try {
      const { error: updateError } = await supabase
        .from('messages')
        .update({ is_approved: !currentStatus })
        .eq('id', messageId);
        
      if (updateError) throw updateError;
      
      setMessages(messages.map(message =>
        message.id === messageId
          ? { ...message, is_approved: !currentStatus }
          : message
      ));
      setError(null);
    } catch (err) {
      console.error('Error toggling message approval:', err);
      throw new Error('Failed to update message approval status');
    }
  };

  const deleteMessage = async (messageId: string) => {
    try {
      const { error: deleteError } = await supabase
        .from('messages')
        .delete()
        .eq('id', messageId);
        
      if (deleteError) throw deleteError;
      
      setMessages(messages.filter(message => message.id !== messageId));
      setError(null);
    } catch (err) {
      console.error('Error deleting message:', err);
      throw new Error('Failed to delete message');
    }
  };

  const approveAll = async () => {
    try {
      const { error: updateError } = await supabase
        .from('messages')
        .update({ is_approved: true })
        .eq('is_approved', false);
        
      if (updateError) throw updateError;
      
      setMessages(messages.map(message => ({
        ...message,
        is_approved: true
      })));
      setError(null);
    } catch (err) {
      console.error('Error approving all messages:', err);
      throw new Error('Failed to approve all messages');
    }
  };

  return {
    messages,
    loading,
    error,
    toggleApproval,
    deleteMessage,
    approveAll,
    refreshMessages: fetchMessages
  };
}